/*
 * COPYRIGHT: EUMETSAT 2018
 * LICENSE: PROPRIETARY
 * PROJECT: WMS
 *
 * All information contained herein is, and remains the property of EUMETSAT.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * EUMETSAT.
 */

import { Injectable }  from '@angular/core';

import { BehaviorSubject }  from 'rxjs/BehaviorSubject';

import { OAuthService, JwksValidationHandler } from 'angular-oauth2-oidc';

import { ConfigService }  from '../../../config/config.service';
import { OAuthScopedStorage } from './oauth-scoped-storage';


@Injectable()
export class UserService {

    private enableSSO = false;
    private loggedUser = new BehaviorSubject(null);
    private authInitSuccess;
    private accessToken = new BehaviorSubject(null);

    constructor(
        authStorage: OAuthScopedStorage,
        private oauthService: OAuthService,
        private configService: ConfigService
    ) {

        let authConfig = configService.config.auth;

        this.oauthService.setStorage(authStorage);
        this.oauthService.configure(authConfig);

        this.oauthService.silentRefreshRedirectUri = this.getBaseHref() + authConfig.silentRefreshRedirectUri;
        this.oauthService.setupAutomaticSilentRefresh();

        this.oauthService.events.subscribe( (event) => {
            if (event.type === 'token_received') {
                this.updateUserData();
            } else if (event.type === 'logout') {
                this.updateUserData();
            } else if (event.type === 'silent_refresh_error') {

            } else if (event.type === 'silent_refresh_timeout') {
                //https://wso2.org/jira/browse/IDENTITY-5581
                //this.oauthService.logOut(true);
            }
            console.log('oauth/oidc event', event);
        });

        this.oauthService.tokenValidationHandler = new JwksValidationHandler();

        this.authInitSuccess = this.oauthService.loadDiscoveryDocument(
            authConfig.discoveryDocumentUrl
        ).then(() => {

            //bug in WSO2 (end_session_endpoint and check_session_iframe are not provided in discovery document)
            //https://wso2.org/jira/browse/IDENTITY-6765
            this.oauthService.logoutUrl = authConfig.logoutUrl;
            this.oauthService.postLogoutRedirectUri = authConfig.postLogoutRedirectUrl;

            /*
            //bug in session check with regex in redirect URL
            //https://wso2.org/jira/browse/IDENTITY-5424
            this.oauthService.sessionCheckIFrameUrl =
                `${clientAuthConfig.sessionCheckIFrameUrl}?client_id=${clientAuthConfig.clientId}&redirect_uri=${this.routeService.getBaseHref()}`;
            this.oauthService.sessionChecksEnabled = true;
            this.oauthService.setupAutomaticSilentRefresh();
            */

            return this.oauthService.tryLogin().then(() => {
                if (!this.isLogged()) {
                    //silent check for existing WSO2 session
                    return this.tryRefreshSession().then((success) => {
                        if (!success) {
                            return new Promise(() => (this.login()));
                        } else {
                            return true;
                        }
                    });
                } else {
                    this.updateUserData();
                    return true;
                }

            });
        }).catch((e) => {
            return false;
        });
    }

    isAuthInitSuccess() {
        return this.authInitSuccess;
    }

    isLogged() {
        return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
    }

    getLoggedUser() {
        return this.loggedUser.asObservable();
    }

    getCurrentUser() {
        return this.loggedUser.value;
    }

    getAccessToken() {
        return this.accessToken.value;
    }

    getAccessTokenObs() {
        return this.accessToken.asObservable();
    }

    login(redirectUrl?: string) {
        this.oauthService.redirectUri = redirectUrl || window.location.href;
        this.oauthService.initImplicitFlow();
    }

    logout(disableRedirect?: boolean) {
        let iframe = document.createElement('iframe');

        iframe.onload = ((evt) => {
            this.oauthService.postLogoutRedirectUri = this.getBaseHref();
            this.oauthService.logOut(disableRedirect);
        });
        iframe.setAttribute('src', this.configService.config.auth.postLogoutRedirectUrl);
        iframe.style['display'] = 'none';
        document.body.appendChild(iframe);

    }

    shouldRestoreStateFromSession() {
        try {
            let state = JSON.parse(this.oauthService.state);
            return state && state.shouldRestoreStateFromSession;
        } catch (e) {
            return false;
        }
    }

    tryRefreshSession() {
        //try a silent refresh
        return this.oauthService.silentRefresh()
            .then((info) => {
                return true;
            })
            .catch((err) => {
                return false;
            });
    }

    private updateUserData() {

        this.accessToken.next(this.oauthService.getAccessToken());

        let idClaim: any = this.oauthService.getIdentityClaims();
        if (idClaim) {
            if (!this.loggedUser.value || this.loggedUser.value.userId !== idClaim.sub) {
                this.loggedUser.next({
                    userId: idClaim.sub,
                    hasLicense: idClaim.has_license ? idClaim.has_license.toLowerCase() === 'true' : false,
                    email: idClaim.email
                });
            }
        } else {
            if (this.loggedUser.value) {
                this.loggedUser.next(null);
            }
        }
    }

    private getBaseHref() {
        return document.getElementsByTagName('base')[0].href;
    }
}



// WEBPACK FOOTER //
// ./src/main/services/user/user.service.ts